// Public records only. Customer submissions use Shopify's native contact form;
// nothing is published, stored locally, or added to the rating in this browser.
export const REVIEW_COUNTRIES = Object.freeze({
  GB: { label: 'United Kingdom', flag: '🇬🇧' },
  'GB-WLS': { label: 'Wales', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿' },
  IE: { label: 'Ireland', flag: '🇮🇪' },
  PL: { label: 'Poland', flag: '🇵🇱' },
});

export function selectReviews(records, { rating = 'all', sort = 'newest' } = {}) {
  return records.filter(review => rating === 'all' || review.rating === Number(rating)).sort((a, b) => {
    const dateOrder = String(b.date || '').localeCompare(String(a.date || ''));
    if (sort === 'highest') return b.rating - a.rating || dateOrder;
    if (sort === 'lowest') return a.rating - b.rating || dateOrder;
    return dateOrder;
  });
}

const element = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

class CustomerReviews extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    let records;
    try { records = JSON.parse(this.querySelector('[data-review-records]')?.textContent || '[]'); }
    catch { return; } // Preserve the server-rendered reviews if data is unavailable.
    if (!Array.isArray(records) || !records.length) return;
    this.records = records;
    this.list = this.querySelector('[data-review-list]');
    this.filter = this.querySelector('[data-review-filter]');
    this.sort = this.querySelector('[data-review-sort]');
    this.more = this.querySelector('[data-review-more]');
    this.status = this.querySelector('[data-review-status]');
    if (!this.list || !this.filter || !this.sort || !this.more || !this.status) return;
    this.limit = 6;
    const options = { signal: this.abort.signal };
    [this.filter, this.sort].forEach(control => control.addEventListener('change', () => {
      this.limit = 6;
      this.render();
    }, options));
    this.more.addEventListener('click', () => {
      const previousCount = this.list.children.length;
      this.limit += 6;
      this.render();
      const firstNew = this.list.children[previousCount];
      if (firstNew) { firstNew.tabIndex = -1; firstNew.focus({ preventScroll: true }); }
    }, options);
    this.querySelector('[data-review-tools]').hidden = false;
    this.render();
  }

  disconnectedCallback() { this.abort?.abort(); }

  render() {
    const reviews = selectReviews(this.records, { rating: this.filter.value, sort: this.sort.value });
    const shown = reviews.slice(0, this.limit);
    this.list.replaceChildren(...shown.map(review => this.card(review)));
    this.status.textContent = reviews.length ? `Showing ${shown.length} of ${reviews.length} reviews` : 'No reviews match this rating.';
    this.more.hidden = shown.length >= reviews.length;
  }

  card(review) {
    const card = element('article', 'rl-customer-review');
    const header = element('header');
    const author = element('div', 'rl-customer-review__author');
    author.append(element('strong', '', review.author));
    const country = Object.hasOwn(REVIEW_COUNTRIES, review.country_code) ? REVIEW_COUNTRIES[review.country_code] : null;
    if (country) {
      const flag = element('span', 'rl-customer-review__flag', country.flag);
      flag.setAttribute('role', 'img');
      flag.setAttribute('aria-label', country.label);
      flag.title = country.label;
      author.append(flag);
    }
    header.append(author);
    if (review.verified === true) header.append(element('span', 'rl-customer-review__verified', 'Verified purchase'));
    if (review.date) {
      const date = new Date(`${review.date}T12:00:00Z`);
      const time = element('time', '', Number.isNaN(date.valueOf()) ? review.date : new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date));
      time.dateTime = review.date;
      header.append(time);
    }
    const stars = element('span', 'rl-rating-stars');
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
    const empty = element('span', 'rl-rating-stars__empty', '★★★★★');
    const fill = element('span', 'rl-rating-stars__fill', '★★★★★');
    empty.setAttribute('aria-hidden', 'true');
    fill.setAttribute('aria-hidden', 'true');
    fill.style.width = `${Math.max(0, Math.min(5, review.rating)) * 20}%`;
    stars.append(empty, fill);
    card.append(header, stars);
    if (review.title) card.append(element('h4', '', review.title));
    if (review.body.length > 240) {
      const details = element('details', 'rl-customer-review__more');
      const summary = element('summary');
      summary.append(element('span', '', `${review.body.slice(0, 237)}…`), element('span', 'rl-customer-review__read-more', 'Read full review'));
      details.append(summary, element('p', '', review.body));
      card.append(details);
    } else card.append(element('p', '', review.body));
    return card;
  }
}

if (!customElements.get('rl-customer-reviews')) customElements.define('rl-customer-reviews', CustomerReviews);
